/**
 * @Description 商圈内所有集客点信息
 */

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col, Anchor } from 'antd';
import V2Tabs from '@/common/components/Data/V2Tabs';
import V2DetailItem from '@/common/components/Feedback/V2DetailItem';
import V2DetailGroup from '@/common/components/Feedback/V2DetailItem/Group';
import V2Title from '@/common/components/Feedback/V2Title';
import V2Anchor from '@/common/components/Others/V2Anchor';
import { beautifyThePrice, isArray } from '@lhb/func';
import styles from './index.module.less';
import cs from 'classnames';
import LinesInfo from './LinesInfo';
import { useMethods } from '@lhb/hook';

interface Props {
  detail; // 集客点详情
  defaultPlanSpotId?: any; // 默认展示的集客点
}

const { Link } = Anchor;

const CollectionPointInfo: FC<Props> = ({
  detail,
  defaultPlanSpotId
}) => {

  const collectionPointInfo: any = useRef(null);
  const [activeKey, setActiveKey] = useState<string>('');

  // 构建children
  const ConstructChildren = ({ index, isSingle = false }) => {
    const data = detail?.planSpots[index];
    return (
      <div className={styles.collectionPointInfo}>
        <div className={styles.spotInfo}>
          <PlanSpotInfo info={data} isSingle={isSingle}/>
        </div>
        <div className={styles.link}>
          <V2Anchor
            getContainer={() => {
              const target: any = collectionPointInfo.current || document.body;
              return target;
            }}
          >
            <Link href='#planSpotInfo' title='集客点信息' />
            {data?.competitors?.length ? <Link href='#competitorsInfo' title='竞品分析' /> : <></>}
            <Link href='#landlordsInfo' title='店铺信息' />

            {/* {anchorItems.map(item => <Link key={item.title} href={`#${item.id}`} title={item.title} />)} */}
          </V2Anchor>
        </div>
      </div>
    );
  };

  // 构建集客点信息
  const PlanSpotInfo = ({ info: data, isSingle }) => {
    const { competitors, landlords } = data; // 竞品信息，房东店铺信息
    // 单个集客点时，因为没有tabbar，只能在此处展示标签
    const firstLabel = isSingle && isArray(data?.labels) && data.labels.length && data.labels[0].name ? data.labels[0] : null;

    return (
      <div>
        <V2Title
          id='planSpotInfo'
          divider type='H2'
          text='集客点信息'
          extra={firstLabel ? methods.renderSpotLabel(firstLabel) : <></>}
          style={firstLabel ? { justifyContent: 'flex-start' } : {}}
        />
        <LinesInfo detail={detail} />
        {/* 集客点信息 */}
        <div>
          <V2DetailGroup block>
            <Row gutter={24}>
              <Col span={12}>
                <V2DetailItem label='集客点名称' value={data?.name} />
              </Col>
              <Col span={12}>
                <V2DetailItem label='动线始末' value={data?.editDescription} />
              </Col>
              <Col span={12}>
                <V2DetailItem label='首选店铺' value={data?.firstShop} />
              </Col>
              <Col span={12}>
                <V2DetailItem label='次选店铺' value={data?.secondShop} />
              </Col>
              {/* <Col span={12}>
                <V2DetailItem label='集客A类点' value={data?.pointName} />
              </Col> */}
              <Col span={12}>
                <V2DetailItem
                  label='租金单价'
                  value={data.rent ? beautifyThePrice(data.rent, ',', 2) + '㎡/月/元' : '-'}
                />
              </Col>
              <Col span={12}>
                <V2DetailItem
                  label='转让费'
                  value={data.assignmentFee ? beautifyThePrice(data.assignmentFee, ',', 2) + '元' : '-'} />
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={12}>
                <V2DetailItem type='videos' label='动线视频讲解' assets={data?.videoUrls} />
              </Col>
              {/* <Col span={12}>
                <V2DetailItem
                  label='预估日均金额'
                  value={data.estimatedDailyAmount ? beautifyThePrice(data.estimatedDailyAmount, ',', 2) + '元' : '-'}
                />
              </Col> */}
              <Col span={12}>
                <V2DetailItem label='视频备注' value={data?.videoRemark} />
              </Col>
            </Row>
          </V2DetailGroup>
        </div>

        {competitors?.length ? <V2Title id='competitorsInfo' divider type='H2' text='竞品信息' /> : <></>}
        {/* 竞品信息 */}

        {competitors ? (
          <V2DetailGroup block>
            {competitors.map((item, index) => (
              <div
                key={item.competitorName + index}
              >
                <Row gutter={20}>
                  <Col span={10}>
                    <V2DetailItem label={`竞品名称${index + 1}`} value={item?.competitorName} />
                  </Col>
                  <Col span={10}>
                    <V2DetailItem
                      label='外卖月总单量'
                      value={
                        item.takeAwayCount
                          ? item.takeAwayCount + '单'
                          : '-'
                      }
                    />
                  </Col>
                </Row>
              </div>
            ))}
          </V2DetailGroup>
        ) : <></>}

        {/* 店铺房东信息 */}
        <V2Title id='landlordsInfo' divider type='H2' text='店铺信息' />
        {landlords ? (
          <V2DetailGroup>
            {landlords.map((item, index) => (
              <div
                key={item.landlordShopName + index}
              >
                <Row gutter={20}>
                  <Col span={12}>
                    <V2DetailItem label='店铺名称' value={item?.landlordShopName} />
                  </Col>
                  <Col span={12}>
                    <V2DetailItem label='联系人姓名' value={item.landlordName} />
                  </Col>
                </Row>
                <Row gutter={20}>
                  <Col span={12}>
                    <V2DetailItem label='联系方式' value={item?.landlordMobile} />
                  </Col>
                </Row>
              </div>
            ))}
          </V2DetailGroup>
        ) : <></>}
      </div>
    );
  };

  const methods = useMethods({
  /**
   * @description 渲染集客点标签
   * @param labelInfo 标签对象{id, name}
   * @return
   */
    renderSpotLabel(labelInfo: any) {
      const color = labelInfo?.name === '已签约' ? '#02C681' : '#FF861D';
      return <span className={cs(styles.planSpotLabel, 'ml-2')} style={{ color, borderColor: color }}>{labelInfo?.name || ''}</span>;
    },
  });

  // 一级菜单
  const tabs = useMemo(() => {
    if (!Object.keys(detail).length) return [];
    const { planSpots } = detail;
    if (isArray(planSpots) && planSpots.length) {
      return planSpots.map((item: any, index) => {
        const firstLabel = isArray(item?.labels) && item.labels.length && item.labels[0].name ? item.labels[0] : null;
        return {
          label: firstLabel ? <div className={styles.planSpotTab}>
            {`集客点${index + 1}`}
            {methods.renderSpotLabel(firstLabel)}
          </div> : `集客点${index + 1}`,
          key: `${item.id}`, // tabs组件的activeKey是string类型
          children: (
            <ConstructChildren key={item.name + index} index={index} isSingle={planSpots.length === 1} />
          )
        };
      });
    }
    return [];
  }, [detail]);

  useEffect(() => {
    if (!isArray(tabs) || !tabs.length) {
      setActiveKey('');
      return;
    };
    if (defaultPlanSpotId && tabs.some((itm: any) => itm.key === `${defaultPlanSpotId}`)) { // 有指定默认tab
      setActiveKey(`${defaultPlanSpotId}`);
    } else { // 没有指定使用第一个
      setActiveKey(tabs[0]?.key || '');
    }
  }, [tabs]);

  return (
    <div ref={collectionPointInfo}>
      {/* 在这里放置你的组件内容 */}
      <V2Tabs
        items={tabs}
        activeKey={activeKey}
        onChange={(key: any) => setActiveKey(key)}
        className={tabs?.length === 1 ? styles.singleTab : ''}
      />
    </div>
  );
};

export default CollectionPointInfo;
